import { Flex, Table, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import { ImpressaoContainer, ImpressaoHeader } from "components/Impressao";
import type { ApiCalculo } from "pages/calculo/types/ApiCalculo";
import type { ApiCalculoComposicao } from "pages/calculo/types/ApiCalculoComposicao";
import { decimalFormater, numberToCurrency } from "std/format";
import { roundAbnt } from "std/math";

const { Title } = Typography;

const ITEM_DECIMAL_SCALE = 4;

export const columns: ColumnType<ApiCalculo>[] = [
	{
		dataIndex: ["produtor", "for_codigoindividual"],
		key: "for_codigoindividual",
		title: "Código",
		align: "center",
		render: (for_codigoindividual) => <>{for_codigoindividual || ""}</>,
	},
	{
		dataIndex: ["produtor", "pessoa", "pes_nome_razaosocial"],
		key: "pes_nome_razaosocial",
		title: "Produtor",
		render: (produtor) => <>{produtor || ""}</>,
	},
	{
		dataIndex: "calc_volume",
		key: "calc_volume",
		title: "Volume Total",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
	{
		dataIndex: "calc_valor_unit",
		key: "calc_valor_unit",
		title: "Vlr. Unitário",
		align: "right",
		render: (value) => numberToCurrency(roundAbnt(value || 0, ITEM_DECIMAL_SCALE), ITEM_DECIMAL_SCALE),
	},
	{
		dataIndex: "calc_valor_total",
		key: "calc_valor_total",
		title: "Total",
		align: "right",
		render: (value) => numberToCurrency(value || 0, 3),
	},
];

export const columnsItens = (totalVolume: number): ColumnType<ApiCalculoComposicao>[] => [
	{
		dataIndex: "calc_comp_indicador",
		key: "calc_comp_indicador",
		title: "Item",
		width: "30%",
	},
	{
		dataIndex: "calc_comp_descricao",
		key: "calc_comp_descricao",
		title: "Descrição",
		width: "30%",
	},
	{
		dataIndex: "calc_comp_valor",
		key: "calc_comp_valor",
		title: "Valor",
		align: "right",
		width: "20%",
		render: (value) => numberToCurrency(value || 0, ITEM_DECIMAL_SCALE),
	},
	{
		dataIndex: "calc_comp_valor",
		key: "valor_total",
		title: "Valor Total",
		align: "right",
		width: "20%",
		render: (value) => numberToCurrency(roundAbnt((value || 0) * (totalVolume || 0), ITEM_DECIMAL_SCALE), ITEM_DECIMAL_SCALE),
	},
];

export function TableRelEntradaLeite({ data, title }: { data: ApiCalculo[] | undefined; title: string }) {
	const totalVolume = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_volume, 0));
	const totalValor = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_valor, 0));

	const TableItens = (itens: ApiCalculoComposicao[], totalVolume: number): JSX.Element => (
		<div style={{ paddingBottom: "18px", paddingTop: "4px" }}>
			<Table
				rowKey="calc_comp_idpk"
				bordered={true}
				columns={columnsItens(totalVolume)}
				dataSource={itens}
				pagination={false}
				summary={(rows) => {
					const totalPreco = roundAbnt(
						(rows || []).reduce((acc, cur) => roundAbnt(acc + (cur.calc_comp_valor || 0), ITEM_DECIMAL_SCALE), 0),
						ITEM_DECIMAL_SCALE,
					);

					const totalValor = roundAbnt(
						(rows || []).reduce((acc, cur) => acc + roundAbnt((cur.calc_comp_valor || 0) * (totalVolume || 0), ITEM_DECIMAL_SCALE), 0),
						ITEM_DECIMAL_SCALE,
					);

					return (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} colSpan={3} align="right">
								<Flex gap={"12px"} justify="end">
									<div>Total:</div>
									<div style={{ margin: "0px" }}>{numberToCurrency(totalPreco, ITEM_DECIMAL_SCALE)}</div>
								</Flex>
							</Table.Summary.Cell>

							<Table.Summary.Cell index={0} colSpan={1} align="right">
								<Flex gap={"12px"} justify="end">
									<div>Total:</div>
									<div style={{ margin: "0px" }}>{numberToCurrency(totalValor, ITEM_DECIMAL_SCALE)}</div>
								</Flex>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					);
				}}
			/>
		</div>
	);

	return (
		<ImpressaoContainer className="w-full mt-3" title={title}>
			<ImpressaoHeader title={title} />

			<Table
				rowKey="calc_idpk"
				columns={columns}
				dataSource={data}
				pagination={false}
				size="small"
				rowClassName="zebra"
				expandable={{
					expandedRowRender: (record) => (
						<Flex vertical={true}>
							<div>{TableItens(record?.composicao || [], record?.calc_volume)}</div>
						</Flex>
					),
					defaultExpandAllRows: true,
				}}
				summary={(rows) => (
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} align="left" colSpan={2}>
							<div style={{ display: "flex" }}>
								<Flex vertical={true}>
									<Title level={5}>Qtd. Produtores:</Title>
									<Title level={5} style={{ margin: "0px", textAlign: "right" }}>
										{rows?.length || 0}
									</Title>
								</Flex>
							</div>
						</Table.Summary.Cell>

						<Table.Summary.Cell index={1} colSpan={2} align="right">
							<Flex vertical={true}>
								<Title level={5}>Volume:</Title>
								<Title level={5} style={{ margin: "0px" }}>
									{decimalFormater(totalVolume, 3)}
								</Title>
							</Flex>
						</Table.Summary.Cell>

						<Table.Summary.Cell index={1} align="right">
							<Flex vertical={true}>
								<Title level={5}>Média:</Title>
								<Title level={5} style={{ margin: "0px" }}>
									{numberToCurrency(roundAbnt(totalValor / totalVolume, ITEM_DECIMAL_SCALE), ITEM_DECIMAL_SCALE)}
								</Title>
							</Flex>
						</Table.Summary.Cell>

						<Table.Summary.Cell index={1} align="right">
							<Flex vertical={true}>
								<Title level={5}>Total Pago:</Title>
								<Title level={5} style={{ margin: "0px" }}>
									{numberToCurrency(totalValor, 3)}
								</Title>
							</Flex>
						</Table.Summary.Cell>
					</Table.Summary.Row>
				)}
			/>
		</ImpressaoContainer>
	);
}
